import {CesiumAdapterService} from '../../services/cesiumAdapter21/cesium-adapter.service';
import {Label} from './Label';
import {Billboard, BillboardCollection} from 'cesium';
import {ItemsManagerInterface} from '../ItemsManager/ItemsManagerInterface';

export class CanvasLabelsManager implements ItemsManagerInterface {
  private cesiumAdapterService: CesiumAdapterService = undefined;
  private labelCollectionSize: number = 20;

  private items = new Map<string, Label>();
  private billboards = new Map<string, Billboard>();
  private canvases = new Map<string, HTMLCanvasElement>();
  private billboardCollection: BillboardCollection = undefined;
  // private billboardCollections = [];

  private activeIds: Set<string> = new Set<string>();
  private needToShowIds: Set<string> = new Set<string>();

  private isDrawn = false;

  private decreaseStartLevel = 20; // 18 zoom level
  private minShowLevel = 18; // 16 zoom level
  private isSizeInMeters = false;

  private itemsSizeScale = 1;

  constructor(cesiumAdapterService) {
    this.cesiumAdapterService = cesiumAdapterService;

    this.billboardCollection = this.cesiumAdapterService.viewer.scene.primitives.add(
      new Cesium.BillboardCollection({
        blendOption: Cesium.BlendOption.OPAQUE_AND_TRANSLUCENT,
      })
    );
    // this.billboardCollections.push(this.billboardCollection);

    if (window['BILLBOARD_LABELS_DISAPPEAR_ZOOM_LEVEL']) {
      // +2 because of our zoom level height specific
      this.minShowLevel = window['BILLBOARD_LABELS_DISAPPEAR_ZOOM_LEVEL'] + 2;
    }

    if (window['BILLBOARD_LABELS_SIZE_IN_METERS']) {
      this.isSizeInMeters = window['BILLBOARD_LABELS_SIZE_IN_METERS'];
    }
  }

  private createTestItems(start,  count, latitude, longitude, area) {
    const leftBottom = {latitude, longitude};
    const styles = ['default', 'ebony', 'vanilla', 'primary', 'white', 'black'];

    for (let i = 0; i < count; i++) {
      this.setItem({
        id: (i + start).toString(),
        position: {latitude: leftBottom.latitude + Math.random() * area, longitude: leftBottom.longitude + Math.random() * area},
        anchor: Label.CENTER,
        value: 'Label ' + i,
        style: styles[ Math.floor(Math.random() * 6 - 0.01) ],
      });
    }
  }

  public hasItem(id) {
    return this.activeIds.has(id);
  }

  public setItem(itemConfig) {
    const item = new Label(itemConfig);
    const itemId = item.getId();
    // console.log('[CANVAS LABELS] setItem', itemId);

    if (item.isEmpty()) {
      if (this.activeIds.has(itemId)) {
        this.deleteItemById(itemId);
      }
      return;
    }

    const curItem = this.items.get(itemId);
    if (curItem && curItem.isEqual(item)) {
      // console.log('[CANVAS LABELS] setItem, equal');
      if (!this.activeIds.has(itemId)) {
        this.activeIds.add(itemId);
        const billboard = this.billboards.get(itemId);
        if (billboard && !billboard.show) {
          // console.log('[CANVAS LABELS] setItem, show billboard id', itemId, billboard.show);
          if (!this.isDrawn) {
            this.needToShowIds.add(itemId);
          }
          billboard.show = this.isDrawn;
          this.requestRender();
        }
      }
      return;
    }

    let isEqualGraphic = false;
    if (curItem) {
      isEqualGraphic = curItem.isEqualGraphic(item);
      // console.log('[CANVAS LABELS] set billboard, update', itemId, isEqualGraphic, curItem.getGraphicDataAsString(), item.getGraphicDataAsString());
    }

    this.items.set(itemId, item);
    this.activeIds.add(itemId);

    this.draw(item, isEqualGraphic);
  }

  public hideItemByValue(itemConfig) {
    // console.log('[CANVAS LABELS] hideItemByValue(itemConfig)', itemConfig);
    const itemId = Label.getIdStatic(itemConfig);
    this.hideItemById(itemId);
  }

  private hideItemById(id) {
    // console.log('[CANVAS LABELS] deleteItemById(id)', id);
    if (this.activeIds.has(id)) {
      this.activeIds.delete(id);
    }

    // TODO: possible to crate function "erase"
    const billboard = this.billboards.get(id);
    if (billboard) {
      billboard.show = false;
      this.requestRender();
      // console.log('[CANVAS LABELS] hideItemById(id) hide billboard', id, billboard);
    }
  }

  public deleteItemByValue(itemConfig) {
    // console.log('[CANVAS LABELS] deleteItemByValue(itemConfig)', itemConfig);
    const itemId = Label.getIdStatic(itemConfig);
    this.deleteItemById(itemId);
  }

  private deleteItemById(id) {
    this.hideItemById(id);

    const billboard = this.billboards.get(id);
    if (billboard) {
      // console.log('[CANVAS LABELS] deleteItemById(id) hide billboard', id, billboard);

      this.billboards.delete(id);
      // @ts-ignore
      billboard.dynamicBillboardCollection.remove(billboard);
      Cesium.destroyObject(billboard);
    }

    if (this.canvases.has(id)) {
      this.canvases.delete(id);
      //possible also delete document canvas there (need to add id to canvas)
    }

    if (this.items.has(id)) {
       this.items.delete(id);
    }
  }

  private draw(item: Label, isEqualGraphic) {
    const id = item.getId();
    let billboard = this.billboards.get(id);

    let canvas;
    if (this.canvases.has(id)) {
      // console.log('[CANVAS LABELS] draw, get canvas', id);
      canvas = this.canvases.get(id);
    } else {
      // console.log('[CANVAS LABELS] draw, create canvas');
      canvas = document.createElement('canvas');
      this.canvases.set(id, canvas);
    }

    let context;
    let bilboardSize;
    let canvasSize;
    if (!isEqualGraphic || !id || !billboard) {
      context =  canvas.getContext("2d");
      canvasSize = item.draw(context, true);
      bilboardSize = this.canvasSizeToBillboardSize(canvasSize, this.isSizeInMeters);
      // console.log('[CANVAS LABELS] draw, calc size', canvasSize, bilboardSize);
    }

    if (!isEqualGraphic) {
      // console.log('[CANVAS LABELS] !isEqualGraphic', isEqualGraphic);
      canvas.width = Math.floor(canvasSize.x);
      canvas.height = Math.floor(canvasSize.y);
      context.clearRect(0, 0, canvas.width, canvas.height);
      item.draw(context);
    }

    let latLng = item.getLatLng();
    latLng = item.getAnchorOffset(latLng);
    const pixelOffset = {x: 0, y: 0};
    //const pixelOffset = this.cesiumAdapterService.getOffsetByAnchor(item.getAnchor());

    const position = Cesium.Cartesian3.fromDegrees(latLng.lng, latLng.lat, 10000);

    if (!this.isDrawn) {
      this.needToShowIds.add(id);
    }

    if (id && billboard) {
      // console.log('[CANVAS LABELS] update billboard', id, canvasSize, bilboardSize, isEqualGraphic);

      billboard.position = position;
      billboard.pixelOffset = new Cesium.Cartesian2(pixelOffset.x, pixelOffset.y);
      billboard.show = this.isDrawn;

      if (!isEqualGraphic) {
        billboard.width = Math.floor(bilboardSize.x);
        billboard.height = Math.floor(bilboardSize.y);
        // @ts-ignore
        billboard.image = canvas;
      }
    } else {
      // console.log('[CANVAS LABELS] add new billboard', id, canvasSize, bilboardSize);

      if (this.billboardCollection.length > this.labelCollectionSize) {
        this.billboardCollection = this.cesiumAdapterService.viewer.scene.primitives.add(
          new Cesium.BillboardCollection({
            blendOption: Cesium.BlendOption.OPAQUE_AND_TRANSLUCENT,
          })
        );
        // this.billboardCollections.push(this.billboardCollection);
      }

      const scale = this.isSizeInMeters ? 1.0 : this.itemsSizeScale;
      billboard = this.billboardCollection.add({
        id: id,
        position: position,
        show: this.isDrawn,
        image: canvas,
        scale: scale,
        width: bilboardSize.x,
        height: bilboardSize.y,
        sizeInMeters: this.isSizeInMeters, // size in meters

        eyeOffset: new Cesium.Cartesian3(0.0, 0.0, -10000),
        pixelOffset: new Cesium.Cartesian2(pixelOffset.x, pixelOffset.y),

        verticalOrigin : Cesium.VerticalOrigin.CENTER,

        distanceDisplayCondition: new Cesium.DistanceDisplayCondition(
          0,
          this.getZoomLevelHeight(this.minShowLevel)
        ),
        scaleByDistance: new Cesium.NearFarScalar(
          this.getZoomLevelHeight(this.decreaseStartLevel),
          1,
          this.getZoomLevelHeight(this.minShowLevel),
          0.5
        ),

        // translucencyByDistance: new Cesium.NearFarScalar(0, 1.0, 1700, 0.0),
        heightReference: this.isSizeInMeters ? Cesium.HeightReference.NONE : Cesium.HeightReference.RELATIVE_TO_GROUND
      });

      // console.log('[CANVAS LABELS] disapear height', this.cesiumAdapterService.getHeightByZoomLevel(this.minShowLevel));

      // @ts-ignore
      billboard.isSkipEvents = true;
      // @ts-ignore
      billboard.dynamicBillboardCollection = this.billboardCollection;

      this.billboards.set(id, billboard);
    }

    // console.log('[CANVAS LABELS] billboard', id, item, billboard);
    this.requestRender();
  }

  private canvasSizeToBillboardSize(canvasSize, isSizeInMeters) {
    let x;
    let y;

    if (!isSizeInMeters) {
      x = canvasSize.x / 2.5;
      y = canvasSize.y / 2.5;
    } else {
      const m200px = 45;
      const m40px  = 9;

      x = canvasSize.x / 200 * m200px * Label.scale + 0.1;
      y = canvasSize.y / 40 * m40px * Label.scale + 0.1;
    }

    return {x, y};
  }

  public drawAll() {
    if (!this.isDrawn) {
      this.isDrawn = true;

      for (const id of this.needToShowIds) {
        const billboard = this.billboards.get(id);
        if (billboard) {
          billboard.show = true;
        }
      }
      this.needToShowIds = new Set<string>();
    }

    this.cesiumAdapterService.viewer.scene.requestRender();
  }

  public disableDrawMode() {
    this.isDrawn = false;
  }

  private requestRender() {
    if (this.isDrawn) {
      this.cesiumAdapterService.viewer.scene.requestRender();
    }
  }

  public setZoomLevelHeights(zoomLevelHeights = null) {
    for (const billboard of this.billboards.values()) {
      billboard.distanceDisplayCondition = new Cesium.DistanceDisplayCondition(
        0,
        this.getZoomLevelHeight(this.minShowLevel, zoomLevelHeights)
      );
      billboard.scaleByDistance = new Cesium.NearFarScalar(
        this.getZoomLevelHeight(this.decreaseStartLevel, zoomLevelHeights),
        1,
        this.getZoomLevelHeight(this.minShowLevel, zoomLevelHeights),
        0.5
      );
    }
    this.cesiumAdapterService.viewer.scene.requestRender();
  }

  private getZoomLevelHeight(level, levelHeights = null) {
    let result;
    if (levelHeights) {
      result = levelHeights.findLast(item => item.level === level);
    }
    if (result) {
      return result.height;
    } else {
      return this.cesiumAdapterService.getHeightByZoomLevel(level);
    }
  }

  public setSizeScale(scale) {
    this.itemsSizeScale = scale;
    if (this.isSizeInMeters) {
      return;
    }

    /* const oldItemConfigs = [];
    for (const item of this.items.values()) {
      const config = JSON.parse(JSON.stringify((item.getConfig())));
      oldItemConfigs.push(config);
      this.deleteItemById(item.getId());
    }
    for (const itemConfig of oldItemConfigs.values()) {
      this.setItem(itemConfig);
    } */

    /* for (const billboards of this.billboardCollections) {
      const len = billboards.length;
      for (let i = 0; i < len; ++i) {
        const b = billboards.get(i);
        b.scale = scale;
      }
    } */

    for (const billboard of this.billboards.values()) {
      billboard.scale = scale;
    }

    this.cesiumAdapterService.viewer.scene.requestRender();
  }
}
